/**
 *
 * @module serviceException
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/exceptions.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';

/**
 *
 * @class ServiceException
 * @extends {Error}
 * @desc base exception for SDK services
 * @note Interacts with Logging package and active sinks
 *
 */
export default class ServiceException {

    /**
     *
     * @param {Object} options
     * @param {String} [options.transactionId=''] - The identifier allows BAM to lookup the entire operation in DUST
     * @param {Array<ErrorReason>} [options.reasons=[]] - Returned by the upstream service or by the SDK operation
     * @param {Object} [options.exceptionData={}]
     * @param {Number} [options.status=null]
     *
     */
    constructor(options = {}) {

        const { transactionId, reasons, status, exceptionData = {} } = options;

        const defaultName = 'ServiceException';
        const defaultMessage = 'An unexpected error occurred communicating with a service.';

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.transactionId = Check.string(transactionId) ? transactionId : '';

        /**
         *
         * @access public
         * @type {Array<ErrorReason>}
         *
         */
        this.reasons = Check.array(reasons) ? reasons : [];

        /**
         *
         * @access public
         * @type {Number|null}
         *
         */
        this.status = Check.number(status) ? status : null;

        /**
         *
         * @access public
         * @type {String}
         * @desc Sets the name property from the ExceptionReference object.
         * Defaults to ServiceException if no name is provided.
         *
         */
        this.name = Check.nonEmptyString(exceptionData.name) ? exceptionData.name : defaultName;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.message = Check.nonEmptyString(exceptionData.message) ? exceptionData.message : defaultMessage;

        /**
         *
         * @access public
         * @type {Object}
         * @desc Exception information provided to aid in debugging.
         *
         */
        this.data = {
            name: this.name,
            message: this.message
        };

        Error.call(this, this.message);

        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        }

        if (typeof this.stack === 'string') {
            const stack = this.stack.split('\n');
            Logging.error(stack.slice(0, 3).join('\n'), this);
        } else {
            Logging.error('Unknown', this);
        }

    }

}

ServiceException.prototype = Object.create(Error.prototype);

/**
 *
 * @access public
 * @returns {String}
 *
 */
ServiceException.prototype.toString = function () {
    return `${this.name}: ${this.message}`;
};

/**
 *
 * @access public
 * @returns {String}
 *
 */
ServiceException.prototype.inspect = function () {
    return this.toString();
};



// WEBPACK FOOTER //
// ./src/services/exception/serviceException.js